import { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, CircularProgress } from '@material-ui/core';
import { useDispatch } from 'react-redux';
import RefreshIcon from '@mui/icons-material/Refresh';
import useVrackApi from '../../hooks/useVrackApi';
import { setOpenSnackbar } from '../../store/slices/snackbarSlice';

RefreshInvoiceButton.propTypes = {
  // Company ID the new invoice is generated for. Posted as `company_id`.
  companyId: PropTypes.string.isRequired,
  // ISO `YYYY-MM-DD` strings for the selected billing month — inclusive
  // first/last day of the month. Posted as `start_date` / `end_date`.
  startDate: PropTypes.string.isRequired,
  endDate: PropTypes.string.isRequired,
  // Called after the POST succeeds — typically the parent's `refetch` so the
  // billing report reloads with the newly-generated invoice.
  onRefreshed: PropTypes.func
};

RefreshInvoiceButton.defaultProps = {
  onRefreshed: () => {}
};

export default function RefreshInvoiceButton({ companyId, startDate, endDate, onRefreshed }) {
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const openSnackbar = (payload) => dispatch(setOpenSnackbar(payload));
  const { fetchData: createInvoiceApi } = useVrackApi('post', '/invoice');

  const handleRefresh = async () => {
    setLoading(true);
    try {
      await createInvoiceApi({
        data: {
          company_id: companyId,
          start_date: startDate,
          end_date: endDate
        }
      });
      openSnackbar({ message: 'Invoice generated successfully.' });
      if (onRefreshed) onRefreshed();
    } catch (error) {
      let message;
      if (error?.response?.status === 400) {
        message = 'Invalid invoice request. Check the billing month.';
      } else if (error?.response?.status === 404) {
        message = 'No billable resources found for this company in the selected month.';
      } else {
        message = 'Failed to generate invoice.';
      }
      openSnackbar({ message, severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="contained"
      color="primary"
      startIcon={
        loading ? (
          <CircularProgress size={16} color="inherit" data-testid="refresh-invoice-spinner" />
        ) : (
          <RefreshIcon />
        )
      }
      onClick={handleRefresh}
      disabled={loading}
      data-testid="refresh-invoice-button"
    >
      {loading ? 'Generating…' : 'Regenerate'}
    </Button>
  );
}
